import type { AttributionBand } from '@/lib/ai-marker-attribution/types';
import type { RepositoryPermission } from '@/lib/github/graphql/types';
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { MergeMethod } from '@/lib/pull-requests/types';
import type { CommitCheckSuites, Reactions } from '@/types/github';

import type { PrV2CheckSummary } from './check-run-presentation';
import type { PrV2Label, PrV2MetadataSummary } from './metadata-summary';
import type { TimelineItem } from './timeline-types';

export const PR_V2_GITHUB_REVIEW_SIDES = ['LEFT', 'RIGHT'] as const;
export type PrV2GithubReviewSide = (typeof PR_V2_GITHUB_REVIEW_SIDES)[number];

export type StableViewKeyEntry = {
  scopeKey: string;
  viewKey: string;
};

export type PrV2SummaryResponse = {
  syncState: PrV2SyncState;
  /**
   * Timestamp for when diff-oriented routes successfully revalidated this summary against the live
   * GitHub pull-request payload during the current request. Unlike `pullRequest.cachedAt`, which is
   * the mirror row's last stored timestamp, and `pullRequest.githubUpdatedAt`, which reflects
   * GitHub's PR update time, this is only a request-scoped freshness marker for SSR hydration.
   */
  liveMetadataRefreshedAt?: string | null;
  pullRequest: {
    id: string;
    owner: string;
    repo: string;
    number: number;
    repositoryId?: number | null;
    installationId: number | null;
    title: string | null;
    state: string;
    draft: boolean;
    merged: boolean;
    url: string | null;
    author: {
      login: string | null;
      avatarUrl: string | null;
    };
    headSha: string;
    baseSha: string;
    cachedAt: string;
    githubUpdatedAt: string | null;
    metadata: unknown;
    metadataSummary: PrV2MetadataSummary;
    counts: {
      reviews: number;
      comments: number;
      checks: number;
    };
  };
  discussionCachedAt: string | null;
  mergeabilityStatus: MergeabilityStatus | null;
};

export type PrV2AiAttributionResponse = {
  available: boolean;
  /** Percentage from 0-100 of PR lines attributed to AI-authored or AI-edited code. */
  aiAttributedRate: number | null;
  /** Percentage from 0-100 of PR lines authored by humans without AI attribution. */
  humanPureRate: number | null;
  /** Percentage from 0-100 of PR lines that started from AI output and were later edited. */
  mixedRate: number | null;
  /** Percentage from 0-100 of PR lines kept as pure AI output. */
  aiPureRate: number | null;
  /** Distinct AI agent + model combinations used in this PR, with line contribution. */
  tools: { tool: string; model: string; linesAdded: number }[];
  /**
   * Agents detected from commit/PR text markers (`@/lib/ai-marker-attribution`).
   * A separate, partial-recall estimate: it never feeds `available` or the
   * percentages above, and an empty list means "no marker found", not
   * "human-authored".
   */
  detectedAgents: PrV2MarkerDetectedAgent[];
};

/** Which marker grain(s) produced a detection: commit trailers/identities or PR body/branch. */
export const PR_V2_MARKER_CHANNELS = ['commit', 'pr'] as const;
export type PrV2MarkerChannel = (typeof PR_V2_MARKER_CHANNELS)[number];

export type PrV2MarkerDetectedAgent = {
  /** Slug from the GitAiAuthoring.tool vocabulary, so it matches `tools[].tool`. */
  agentId: string;
  /** Only Claude Code markers carry a model; null otherwise. */
  model: string | null;
  band: AttributionBand;
  channels: PrV2MarkerChannel[];
};

export type PrV2MergeMethod = MergeMethod;

export const PR_V2_MERGE_ACTIONS = ['direct', 'queue', 'bypass'] as const;

/**
 * How a merge request asks GitHub to merge: directly, through the base branch's merge queue, or
 * directly past the branch rules, which also skips a merge queue.
 */
export type PrV2MergeAction = (typeof PR_V2_MERGE_ACTIONS)[number];

/** What the PR page and merge notifications send to the merge route. */
export type PrV2MergeRequest = {
  title: string;
  body?: string | null;
  sha: string;
  merge_method: PrV2MergeMethod;
  merge_action: PrV2MergeAction;
};

/**
 * What GitHub did with a merge request. `pending` means GitHub had not finished when cubic stopped
 * waiting.
 */
export type PrV2MergeOutcome =
  | { status: 'merged'; sha: string }
  | { status: 'enqueued' }
  | { status: 'pending'; uuid: string }
  | { status: 'failed'; message: string };

/** The merge route's success body. A failed merge arrives as an error response instead. */
export type PrV2MergeResponse = Exclude<PrV2MergeOutcome, { status: 'failed' }>;

export const PR_V2_VIEWER_MODE = {
  MEMBER: 'member',
  PUBLIC: 'public',
} as const;

export type PrV2ViewerMode = (typeof PR_V2_VIEWER_MODE)[keyof typeof PR_V2_VIEWER_MODE];

export type PrV2ViewerPermission = RepositoryPermission | null;

export type PrV2MergeabilityCapabilitiesResponse = {
  viewerPermission: PrV2ViewerPermission;
  isAdmin: boolean;
  canBypassProtections: boolean;
  canEnableAutoMerge: boolean;
  syncState: PrV2SyncState;
};

export type PrV2SubmitReviewInput = {
  /**
   * Client-generated UUID identifying this submission attempt. The server uses
   * it to dedupe retries of the same review action.
   */
  submissionId: string;
  review: {
    comment: string;
    reviewType: 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES';
  };
  comments: Array<{
    id: string | number;
    body: string;
    in_reply_to?: string | number;
    commit_id?: string;
    path?: string;
    line?: number;
    side?: PrV2GithubReviewSide;
    subject_type?: 'line' | 'file';
    parent_is_pending?: boolean;
    start_line?: number;
    start_side?: PrV2GithubReviewSide;
  }>;
};

export type PrV2ReviewResponse = {
  id: string;
  githubReviewId: string;
  githubReviewNodeId: string | null;
  state: string | null;
  body: string | null;
  submittedAt: string | null;
  userLogin: string | null;
  userAvatarUrl: string | null;
  commitId: string | null;
  raw: unknown;
};

export type PrV2CommentResponse = {
  id: string;
  githubCommentId: string;
  commentType: PrV2CommentType;
  reactionSubjectType?: PrV2ReactionSubjectType | null;
  githubReviewId: string | null;
  body: string | null;
  path: string | null;
  line: number | null;
  position: number | null;
  inReplyToId: string | null;
  userLogin: string | null;
  userAvatarUrl: string | null;
  createdAtGithub: string | null;
  updatedAtGithub: string | null;
  raw: unknown;
};

export const PR_V2_COMMENT_TYPES = ['issue_comment', 'review_comment'] as const;
export type PrV2CommentType = (typeof PR_V2_COMMENT_TYPES)[number];

export const PR_V2_REACTION_SUBJECT_TYPES = [
  'issue_comment',
  'review_comment',
  'pull_request_review',
] as const;
export type PrV2ReactionSubjectType = (typeof PR_V2_REACTION_SUBJECT_TYPES)[number];

export const PR_V2_REACTION_CONTENTS = [
  'heart',
  '+1',
  '-1',
  'laugh',
  'confused',
  'eyes',
  'rocket',
  'hooray',
] as const;

export type PrV2ReactionContent = (typeof PR_V2_REACTION_CONTENTS)[number];

export type CreatePrV2CommentInput = {
  body: string;
  commentType?: PrV2CommentType;
  inReplyToId?: number;
  path?: string;
  commitId?: string;
  line?: number;
  side?: PrV2GithubReviewSide;
  startLine?: number;
  startSide?: PrV2GithubReviewSide;
  subjectType?: 'line' | 'file';
};

export type UpdatePrV2CommentInput = {
  commentId: number;
  body: string;
  commentType?: PrV2CommentType;
};

/** Label names to add to and remove from a pull request in one change. */
export type UpdatePrV2LabelsInput = {
  add: string[];
  remove: string[];
};

/** The labels on a pull request after a label change. */
export type PrV2LabelsResponse = {
  labels: PrV2Label[];
};

/** A label the repository offers for its pull requests. */
export type PrV2AvailableLabel = PrV2Label & {
  description: string | null;
};

export type PrV2AvailableLabelsResponse = {
  labels: PrV2AvailableLabel[];
};

export const PR_V2_CHECK_RUN_SOURCE = {
  CHECK_RUN: 'check_run',
  STATUS_CONTEXT: 'status_context',
} as const;

export const PR_V2_FAVICON_VISUAL_STATE = {
  Pending: 'pending',
  Success: 'success',
  Failure: 'failure',
} as const;
export type PrV2FaviconVisualState =
  (typeof PR_V2_FAVICON_VISUAL_STATE)[keyof typeof PR_V2_FAVICON_VISUAL_STATE];

export const PR_V2_CHECK_RUN_SOURCES = [
  PR_V2_CHECK_RUN_SOURCE.CHECK_RUN,
  PR_V2_CHECK_RUN_SOURCE.STATUS_CONTEXT,
] as const;
export type PrV2CheckRunSource = (typeof PR_V2_CHECK_RUN_SOURCES)[number];

export type PrV2CheckRunResponse = {
  id: string;
  githubCheckRunId: string;
  source?: PrV2CheckRunSource;
  name: string;
  status: string;
  conclusion: string | null;
  detailsUrl: string | null;
  headSha: string | null;
  startedAt: string | null;
  completedAt: string | null;
  raw: unknown;
};

export type PrV2CubicReviewPreviewResponse = {
  violationId: string;
  githubCommentId: string;
  filePath: string;
  line: number | null;
  endLine: number | null;
  preview: string;
  confidence: number | null;
  severity: number | null;
};

export type PrV2CubicReviewSummaryResponse = {
  totalComments: number;
  outstandingComments: number;
  resolvedComments: number;
  outstandingCommentIds: string[];
  latestActionableViolationIds: string[];
  fixAllOutstandingCount: number;
  latestActionableReviewSessionId: string | null;
  defaultDiscussionCommentId: string | null;
  previewItems: PrV2CubicReviewPreviewResponse[];
  /** Maps githubCommentId → highest severity score for that comment (0-10 scale). */
  severityByCommentId: Record<string, number>;
};

export const PR_V2_STACK_SYNC_STATES = {
  READY: 'ready',
  PARTIAL: 'partial',
} as const;

export type PrV2StackSyncState =
  (typeof PR_V2_STACK_SYNC_STATES)[keyof typeof PR_V2_STACK_SYNC_STATES];

export type PrV2StackPullRequest = {
  id: string;
  number: number;
  title: string | null;
  state: string;
  draft: boolean;
  merged: boolean;
  authorLogin: string | null;
  baseRef: string | null;
  reviewDecision: 'approved' | 'changes_requested' | null;
  checks: PrV2CheckSummary | null;
  requestedReviewerLogins: string[];
};

export type PrV2StackNode = {
  pullRequest: PrV2StackPullRequest;
  children: PrV2StackNode[];
};

export type PrV2StackResponse = {
  isPartOfStack: boolean;
  syncState: PrV2StackSyncState;
  bootstrapDeferred?: boolean;
  stackRoot: PrV2StackNode | null;
};

export type PrV2CubicReviewLocator = {
  owner: string;
  repo: string;
  pullNumber: number;
  githubPRId?: bigint | number | null;
};

export type PrV2DiscussionResponse = {
  syncState: PrV2SyncState;
  timelineItems: TimelineItem[];
  commitCheckSuites: CommitCheckSuites;
  reactions: Record<number, Reactions>;
  optimisticReactionBuckets?: Record<
    number,
    {
      updatedAtMs: number;
      reactions: Reactions;
    }
  >;
  cachedAt: string | null;
};

export type PrV2ReviewThread = {
  id: string;
  isResolved: boolean;
  isOutdated: boolean;
  path?: string | null;
  line?: number | null;
  originalLine?: number | null;
  startLine?: number | null;
  originalStartLine?: number | null;
  diffSide?: PrV2GithubReviewSide | null;
  startDiffSide?: PrV2GithubReviewSide | null;
  resolvedBy?: {
    login: string;
    avatarUrl: string;
  };
  comments: {
    nodes: {
      id: string;
      databaseId?: number | null;
      path?: string | null;
      line?: number | null;
      originalLine?: number | null;
      startLine?: number | null;
      originalStartLine?: number | null;
    }[];
  };
};

export const PR_V2_SYNC_STATES = {
  SYNCING: 'syncing',
  READY: 'ready',
} as const;

export type PrV2SyncState = (typeof PR_V2_SYNC_STATES)[keyof typeof PR_V2_SYNC_STATES];

/**
 * Stable sync kinds for PR v2 follow-up jobs.
 *
 * These values are persisted on sync-job rows, so they stay string-based and intentionally small
 * to avoid drift across producers and consumers.
 */
export const PR_V2_SYNC_KINDS = {
  SUMMARY: 'summary',
  ISSUE_COMMENTS: 'issue_comments',
  REVIEWS: 'reviews',
  REVIEW_COMMENTS: 'review_comments',
  REVIEW_THREADS: 'review_threads',
  TIMELINE: 'timeline',
  CHECKS: 'checks',
  COMMITS: 'commits',
  FILES: 'files',
  MERGEABILITY: 'mergeability',
  REACTIONS: 'reactions',
} as const;

export type PrV2SyncKind = (typeof PR_V2_SYNC_KINDS)[keyof typeof PR_V2_SYNC_KINDS];

export const PR_V2_SYNC_TRIGGERS = {
  WEBHOOK: 'webhook',
  READ_REFRESH: 'read_refresh',
  MANUAL_REFRESH: 'manual_refresh',
  COLD_START: 'cold_start',
  PROJECTION_REPAIR: 'projection_repair',
  BOOTSTRAP: 'bootstrap',
  REACTION_MUTATION: 'reaction_mutation',
  PERMISSION_READ: 'permission_read',
} as const;

export type PrV2SyncTrigger = (typeof PR_V2_SYNC_TRIGGERS)[keyof typeof PR_V2_SYNC_TRIGGERS];

export type ProcessPrV2WebhookDeliveryEventData = {
  provider: 'github';
  deliveryId: string;
  installationId: number | null;
};

export type ProcessPrV2SyncJobEventData = {
  jobId: string;
  installationId: number | null;
};

export type BootstrapPrV2RepoEventData = {
  owner: string;
  repo: string;
  installationId?: number | null;
};
